import type { BoundsType, ItemTransform } from '../core/model';

export const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export const clamp = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

const BOUNDS_TYPES: readonly BoundsType[] = [
  'none',
  'stretch',
  'scale-inner',
  'scale-outer',
  'scale-to-width',
  'scale-to-height',
  'max-only',
];

const finiteVec = (v: { x: number; y: number }): boolean => isNum(v?.x) && isNum(v?.y);

/** Drops non-finite numbers and invalid enum values instead of letting NaN reach the renderer. */
export function sanitizeTransformPatch(patch: Partial<ItemTransform>): Partial<ItemTransform> {
  const out: Partial<ItemTransform> = {};
  if (patch.position && finiteVec(patch.position)) out.position = { x: patch.position.x, y: patch.position.y };
  if (patch.scale && finiteVec(patch.scale)) out.scale = { x: patch.scale.x, y: patch.scale.y };
  if (isNum(patch.rotation)) out.rotation = patch.rotation % 360 || 0;
  if (isNum(patch.alignment)) out.alignment = clamp(Math.trunc(patch.alignment), 0, 15);
  if (isNum(patch.boundsAlignment)) out.boundsAlignment = clamp(Math.trunc(patch.boundsAlignment), 0, 15);
  if (patch.boundsType && BOUNDS_TYPES.includes(patch.boundsType)) out.boundsType = patch.boundsType;
  if (patch.bounds && finiteVec(patch.bounds)) {
    out.bounds = { x: Math.max(0, patch.bounds.x), y: Math.max(0, patch.bounds.y) };
  }
  if (patch.crop) {
    const { left, top, right, bottom } = patch.crop;
    if ([left, top, right, bottom].every(isNum)) {
      out.crop = { left: Math.max(0, left), top: Math.max(0, top), right: Math.max(0, right), bottom: Math.max(0, bottom) };
    }
  }
  return out;
}

const evenDimension = (v: number): number => clamp(Math.round(v / 2) * 2, 16, 7680);

export function sanitizeVideoPatch(patch: { width?: number; height?: number; fps?: number }) {
  const out: { width?: number; height?: number; fps?: number } = {};
  if (isNum(patch.width)) out.width = evenDimension(patch.width);
  if (isNum(patch.height)) out.height = evenDimension(patch.height);
  if (isNum(patch.fps)) out.fps = clamp(Math.round(patch.fps), 1, 120);
  return out;
}

/** Keeps only finite, non-negative values for the named numeric keys; integers are rounded. */
export function sanitizeNonNegative<T extends object>(patch: Partial<T>, keys: readonly (keyof T)[]): Partial<T> {
  const out: Partial<T> = { ...patch };
  for (const key of keys) {
    if (!(key in out)) continue;
    const v = out[key];
    if (isNum(v)) out[key] = Math.max(0, v) as T[keyof T];
    else delete out[key];
  }
  return out;
}
